import { ActionIcon } from "PFComponents/action_icon";
import { Select } from "PFComponents/select";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { ContactInformation, ContactInformationType } from "PFTypes";
import { Option } from "PFTypes/options";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useProfileInformationEditField } from "../../common/use_contact_information_edit_field";
import css from "./contact_information_field.module.scss";

export type ContactInformationInput = ContactInformation & { input_id?: number };

type ContactInformationEditFieldProps = {
  contact: ContactInformationInput;
  error?: string;
  locked?: boolean;
  lockedTip?: string;
  onChange: (contact: ContactInformationInput) => void;
  onRemove: (contact: ContactInformationInput) => void;
};

const ContactInformationEditField = ({
  error,
  locked,
  lockedTip,
  onChange,
  onRemove,
  contact
}: ContactInformationEditFieldProps) => {
  const { t } = useTranslation("profiles", { keyPrefix: "edit.fields" });

  const { typeOptions, getValueLabel } = useProfileInformationEditField();

  const [state, setState] = useState<ContactInformationInput>({
    input_id: contact.input_id,
    type: contact.type,
    value: contact.value,
    label: contact.label
  });

  const handleChange = <K extends "type" | "value" | "label">(
    field: K,
    value: ContactInformationInput[K]
  ) => {
    const newState = { ...state, [field]: value };
    setState(newState);
    onChange?.(newState);
  };

  const handleRemove = () => {
    onRemove?.(contact);
  };

  const { type, value, label } = state;

  const allowLabel = ["phone", "website"].includes(type);

  const valueLabel = getValueLabel(type);

  return (
    <div className={css.root} data-qa-id="profile-edit-contact-info-link">
      <div className={css.type} data-qa-id="select-component-type">
        <Select
          label={t("type")}
          value={type}
          locked={locked}
          lockedTip={lockedTip}
          options={typeOptions}
          dropdownWidth={200}
          onChange={(option) =>
            handleChange("type", (option as Option<string>).value as ContactInformationType)
          }
        />
      </div>
      <div className={css.text} data-qa-id="profile-edit-contact-info-link-input">
        <InputFieldSet
          label={valueLabel}
          value={value}
          error={error}
          onChange={(value) => handleChange("value", value)}
          locked={locked}
          lockedTip={lockedTip}
        />
      </div>
      {allowLabel && (
        <div className={css.text} data-qa-id="profile-edit-contact-info-label-input">
          <InputFieldSet
            label={t("label")}
            value={label}
            onChange={(value) => handleChange("label", value)}
            locked={locked}
            lockedTip={lockedTip}
          />
        </div>
      )}
      {!locked && (
        <div className={css.remove} data-qa-id="profile-edit-contact-info-link-remove">
          <ActionIcon name="remove" onClick={handleRemove} disabled={locked} />
        </div>
      )}
    </div>
  );
};

export default ContactInformationEditField;
